import React, { useCallback, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  FormHelperText,
  Grid as MuiGrid,
  InputLabel,
  Typography,
} from "@mui/material";
import { Sparkles } from "lucide-react";
import moment from "moment";

import RpMuiSelectBase from "@/components/muiCustom/select/RpMuiSelect";
import RpDatePickerBase from "@/components/muiCustom/date/RpDatePicker";
import RpCurrencyFieldBase from "@/components/muiCustom/textField/RpCurrencyField";
import { logger } from "@/helpers/loggerHelper";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { getFormatDate } from "@/helpers/utils";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";
import ChannelFilterSwitchesBase from "../../creatives/ChannelFilterSwitches";
import {
  canSelectPackageWithEnabledChannels,
  getBlockedPackageSelectionInfo,
  getChannelsBlockedFromDisabling,
  pruneSelectedChannelsForPackages,
  shouldPruneChannelsForPackageChange,
} from "@/features/campaigns/shared/helpers/channelSelectionHelper";

const PACKAGE_CHANNEL_SELECTION_ERROR =
  "Enable at least one channel for this package before selecting it.";
const PACKAGE_CHANNEL_TOGGLE_HELPER_TEXT =
  "Channels not selected won’t run as part of the package. Only selected channels will run, and at least one channel must remain selected.";
type BulkSelectionFormValues = {
  creative: Array<string | number>;
  startDate: any;
  endDate: any;
  locationBudget: string | number;
  channels: Array<string | number>;
  useBudgetRecommendation: boolean;
};

type JoinCampaignBulkSelectionFormProps = {
  isBudgetRecommendationAvailable?: boolean;
  creatives?: unknown[];
  onSubmit?: (_values: BulkSelectionFormValues) => Promise<boolean> | boolean;
  campaign?: Record<string, any>;
  hideField: {
    startDate?: boolean;
    endDate?: boolean;
    locationBudget?: boolean;
  };
  subscribedLocations?: unknown[];
  channelSelectionEnabled?: boolean;
  channelOptions?: Array<{ id: string | number; [key: string]: unknown }>;
  creativePackages?: unknown[];
};

const Grid = MuiGrid as unknown as React.ComponentType<Record<string, any>>;
const RpMuiSelect = RpMuiSelectBase as unknown as React.ComponentType<
  Record<string, any>
>;
const RpDatePicker = RpDatePickerBase as unknown as React.ComponentType<
  Record<string, any>
>;
const RpCurrencyField = RpCurrencyFieldBase as unknown as React.ComponentType<
  Record<string, any>
>;
const ChannelFilterSwitches =
  ChannelFilterSwitchesBase as unknown as React.ComponentType<
    Record<string, any>
  >;

const validationSchema = (
  campaign,
  hideField,
  subscribedLocations,
  channelSelectionEnabled
) =>
  Yup.object({
    ...(campaign?.creative_select_flag && campaign?.creative_select_max_value
      ? {
          creative: Yup.array()
            .of(Yup.string())
            .min(1, "Please select at least one creative")
            .max(
              campaign?.creative_select_max_value,
              `You can only select up to ${campaign?.creative_select_max_value} creatives`
            ),
        }
      : {
          creative: Yup.array()
            .of(Yup.string())
            .min(1, "Please select at least one creative")
            .max(1, "Please select only one creative"),
        }),
    startDate: Yup.date()
      [!hideField.startDate ? "required" : "optional"]("Start date is required")
      .test("invalid-start-date", "Enter a valid start date", (value, ctx) => {
        if (!value) return true;

        const { minStartDate } =
          joinCampaignHelper.getCollectedMinMaxStartEndDate(
            subscribedLocations,
            campaign,
            {
              startDate: value,
            }
          );

        if (moment(value).isBefore(moment(minStartDate), "day")) {
          return ctx.createError({
            message: `Start date can not be before ${minStartDate.format("MMM Do, YYYY")}`,
          });
        }

        return true;
      }),
    endDate: Yup.date()
      [!hideField.endDate ? "required" : "optional"]("End date is required")
      .min(Yup.ref("startDate"), "End date must be after start date")
      .test("invalid-end-date", "Enter a valid end date", (value, ctx) => {
        if (!value) return true;

        const { minEndDate } =
          joinCampaignHelper.getCollectedMinMaxStartEndDate(
            subscribedLocations,
            campaign,
            {
              startDate: ctx.parent.startDate,
            }
          );

        if (moment(value).isBefore(moment(minEndDate), "day")) {
          return ctx.createError({
            message: `End date can not be before ${minEndDate.format("MMM Do, YYYY")}`,
          });
        }

        return true;
      }),
    useBudgetRecommendation: Yup.boolean(),
    locationBudget: Yup.number()
      .when("useBudgetRecommendation", {
        is: true,
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema[!hideField.locationBudget ? "required" : "optional"](
            "Location budget is required"
          ),
      })
      .positive("Location budget must be positive")
      .test(
        "min-max-limit",
        "Entered budget is not valid",
        async function (value, ctx) {
          if (!value) return true;

          const { startDate, endDate } = this.parent;

          const currentValue = Number(value);

          // Format dates to YYYY-MM-DD
          const formattedStartDate = startDate
            ? getFormatDate(startDate, "YYYY-MM-DD")
            : null;
          const formattedEndDate = endDate
            ? getFormatDate(endDate, "YYYY-MM-DD")
            : null;

          const isInvalidBudget = joinCampaignHelper.getRequiredMinMax(
            {
              start_date: formattedStartDate,
              end_date: formattedEndDate,
              budget: currentValue,
            },
            campaign
          );

          if (isInvalidBudget) {
            return ctx.createError({
              message: `Please enter a budget between $${isInvalidBudget?.minRequiredBudget} and $${isInvalidBudget?.maxRequiredBudget}`,
            });
          }
          return !isInvalidBudget;
        }
      ),
    ...(channelSelectionEnabled
      ? {
          channels: Yup.array()
            .of(Yup.string())
            .min(1, "Please select at least one channel"),
        }
      : {}),
  });

function JoinCampaignBulkSelectionForm({
  creatives = [],
  onSubmit,
  campaign,
  hideField,
  subscribedLocations,
  channelSelectionEnabled = false,
  channelOptions = [],
  creativePackages = [],
  isBudgetRecommendationAvailable = false,
}: JoinCampaignBulkSelectionFormProps) {
  const formik = useFormik<BulkSelectionFormValues>({
    initialValues: {
      creative: [],
      startDate: "",
      endDate: "",
      locationBudget: "",
      useBudgetRecommendation: false,
      channels: channelOptions.map((channel) => channel.id),
    },
    validationSchema: validationSchema(
      campaign,
      hideField,
      subscribedLocations,
      channelSelectionEnabled
    ),
    validateOnChange: true,
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (shouldBlockUnsafeSubmission(values)) {
          setSubmitting(false);
          return;
        }

        if (typeof onSubmit === "function") {
          const result = await onSubmit(values);
          // Only reset form if onSubmit returns true (successful submission)
          if (result === true) {
            resetForm();
          }
        }
      } catch (error) {
        logger.error("Form submission error:", error);
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const {
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    values,
    isSubmitting,
  } = formik;

  const allLocMinMaxStartEndDate = useMemo(() => {
    const { minStartDate, maxStartDate, minEndDate, maxEndDate } =
      joinCampaignHelper.getCollectedMinMaxStartEndDate(
        subscribedLocations,
        campaign,
        values
      );
    return {
      minStartDate,
      maxStartDate,
      minEndDate,
      maxEndDate,
    };
  }, [subscribedLocations, campaign, values]);

  const handleButtonClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark all fields as touched
    await formik.setTouched({
      creative: true,
      startDate: true,
      endDate: true,
      locationBudget: true,
      useBudgetRecommendation: true,
      channels: true,
    });

    // Validate and submit
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      formik.submitForm();
    }
  };

  const handleCreativeChange = useCallback(
    (v) => {
      const selectedCreatives = Array.isArray(v) ? v : [v];
      const unavailablePackage =
        channelSelectionEnabled &&
        selectedCreatives.find(
          (creativeId) =>
            !canSelectPackageWithEnabledChannels({
              creatives: creativePackages,
              packageId: creativeId,
              selectedChannelIds: values.channels,
            })
        );

      if (unavailablePackage) {
        formik.setFieldError(
          "creative",
          getBlockedPackageSelectionInfo({
            creatives: creativePackages,
            packageId: unavailablePackage,
          }).message || PACKAGE_CHANNEL_SELECTION_ERROR
        );
        setFieldTouched("creative", true);
        return;
      }

      setFieldValue("creative", selectedCreatives);
      if (
        channelSelectionEnabled &&
        shouldPruneChannelsForPackageChange({
          previousPackageIds: values.creative,
          nextPackageIds: selectedCreatives,
        })
      ) {
        setFieldValue(
          "channels",
          pruneSelectedChannelsForPackages({
            creatives: creativePackages,
            selectedPackageIds: selectedCreatives,
            selectedChannelIds: values.channels,
          })
        );
      }
      setFieldTouched("creative", true);
    },
    [
      channelSelectionEnabled,
      creativePackages,
      formik,
      setFieldValue,
      setFieldTouched,
      values.creative,
      values.channels,
    ]
  );

  const handleLocationBudgetChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFieldValue("locationBudget", value);

      // manually validation on change
      formik.validateField("locationBudget");
    },
    [setFieldValue, formik]
  );

  const canUseBudgetRecommendation = useMemo(() => {
    return Boolean(
      isBudgetRecommendationAvailable &&
        values.creative?.length &&
        (hideField.startDate || values.startDate) &&
        (hideField.endDate || values.endDate)
    );
  }, [
    hideField.endDate,
    hideField.startDate,
    isBudgetRecommendationAvailable,
    values.creative,
    values.endDate,
    values.startDate,
  ]);

  const handleBudgetModeChange = useCallback(
    (useBudgetRecommendation) => {
      if (useBudgetRecommendation && !canUseBudgetRecommendation) {
        return;
      }

      setFieldValue("useBudgetRecommendation", useBudgetRecommendation);
      if (useBudgetRecommendation) {
        setFieldValue("locationBudget", "");
        setFieldTouched("locationBudget", false);
      }
    },
    [canUseBudgetRecommendation, setFieldTouched, setFieldValue]
  );

  useEffect(() => {
    if (!canUseBudgetRecommendation && values.useBudgetRecommendation) {
      setFieldValue("useBudgetRecommendation", false);
    }
  }, [
    canUseBudgetRecommendation,
    setFieldValue,
    values.useBudgetRecommendation,
  ]);

  const handleChannelChange = useCallback(
    (selectedChannels) => {
      setFieldValue("channels", selectedChannels);
      setFieldTouched("channels", true);
    },
    [setFieldValue, setFieldTouched]
  );

  const blockedChannelIds = useMemo(() => {
    if (!channelSelectionEnabled) {
      return [];
    }

    return getChannelsBlockedFromDisabling({
      creatives: creativePackages,
      selectedPackageIds: values.creative,
      selectedChannelIds: values.channels,
    });
  }, [
    channelSelectionEnabled,
    creativePackages,
    values.channels,
    values.creative,
  ]);

  return (
    <div className="w-100 bulk-selection-form">
      <div className="bulk-selection-fields-section">
        <Grid container spacing={2}>
          {!hideField.startDate && (
            <Grid
              item
              className="bulk-selection-field"
              size={{
                xs: 12,
                md: 6,
                lg: 3,
              }}
            >
              <InputLabel shrink className="m-0">
                Select Campaign Start Date
              </InputLabel>
              <RpDatePicker
                name="startDate"
                placeholder="Start Date"
                value={values.startDate}
                onChange={(v) => {
                  formik.setValues((prevValues) => ({
                    ...prevValues,
                    startDate: v,
                    endDate: "", // reset end date
                  }));
                }}
                error={touched.startDate && errors.startDate}
                helperText={touched.startDate && errors.startDate}
                onBlur={() => setFieldTouched("startDate", true)}
                minDate={allLocMinMaxStartEndDate.minStartDate}
                maxDate={allLocMinMaxStartEndDate.maxStartDate}
              />
            </Grid>
          )}
          {!hideField.endDate && (
            <Grid
              item
              className="bulk-selection-field"
              size={{
                xs: 12,
                md: 6,
                lg: 3,
              }}
            >
              <InputLabel shrink className="m-0">
                Select Campaign End Date
              </InputLabel>
              <RpDatePicker
                name="endDate"
                placeholder="End Date"
                value={values.endDate}
                onChange={(v) => setFieldValue("endDate", v)}
                error={touched.endDate && errors.endDate}
                helperText={touched.endDate && errors.endDate}
                onBlur={() => setFieldTouched("endDate", true)}
                minDate={allLocMinMaxStartEndDate.minEndDate}
                maxDate={allLocMinMaxStartEndDate.maxEndDate}
              />
            </Grid>
          )}
          <Grid
            item
            className="bulk-selection-field"
            size={{
              xs: 12,
              md: 6,
              lg: 3,
            }}
          >
            <InputLabel shrink className="m-0">
              Select Creative
            </InputLabel>
            <RpMuiSelect
              name="creative"
              placeholder="Creative"
              options={creatives}
              value={values.creative}
              onChange={handleCreativeChange}
              helperText={errors.creative as string}
              onBlur={() => setFieldTouched("creative", true)}
              className="bulk-selection-creative-select"
            />
            {touched.creative && errors.creative && (
              <FormHelperText error>
                {errors.creative as React.ReactNode}
              </FormHelperText>
            )}
          </Grid>
          {!hideField.locationBudget && (
            <Grid
              item
              className={`bulk-selection-field bulk-selection-budget-field ${
                values.useBudgetRecommendation ? "is-recommendation-mode" : ""
              }`}
              size={{
                xs: 12,
                md: 6,
                lg: 3,
              }}
            >
              <InputLabel shrink className="m-0">
                Location Budget
              </InputLabel>
              {!values.useBudgetRecommendation ? (
                <RpCurrencyField
                  name="locationBudget"
                  value={values.locationBudget || ""}
                  onChange={handleLocationBudgetChange}
                  error={Boolean(
                    touched.locationBudget && errors.locationBudget
                  )}
                  helperText={
                    touched.locationBudget
                      ? (errors.locationBudget as string)
                      : ""
                  }
                  onBlur={() => setFieldTouched("locationBudget", true)}
                />
              ) : null}
              {isBudgetRecommendationAvailable ? (
                <>
                  {values.useBudgetRecommendation ? (
                    <div
                      className="bulk-budget-recommendation-mode-banner"
                      data-testid="budget-mode-message"
                    >
                      <Sparkles size={14} strokeWidth={1.8} />
                      <span>
                        Budget recommendations appear below after you apply your
                        selections.
                      </span>
                    </div>
                  ) : null}
                  <div
                    className="bulk-budget-recommendation-toggle"
                    aria-label="Select budget mode"
                    data-testid="budget-mode-toggle"
                  >
                    <button
                      type="button"
                      className={
                        !values.useBudgetRecommendation ? "active" : ""
                      }
                      aria-pressed={!values.useBudgetRecommendation}
                      onClick={() => handleBudgetModeChange(false)}
                    >
                      Manual
                    </button>
                    <button
                      type="button"
                      className={`recommendation-toggle ${
                        values.useBudgetRecommendation ? "active" : ""
                      } ${!canUseBudgetRecommendation ? "is-disabled" : ""}`}
                      aria-pressed={values.useBudgetRecommendation}
                      disabled={!canUseBudgetRecommendation as boolean}
                      onClick={() => handleBudgetModeChange(true)}
                    >
                      <Sparkles size={14} strokeWidth={1.8} />
                      Recommendation
                    </button>
                  </div>
                </>
              ) : null}
            </Grid>
          )}
        </Grid>
      </div>

      {channelSelectionEnabled && (
        <div className="bulk-selection-channel-section">
          <ChannelFilterSwitches
            channels={channelOptions}
            selectedChannelIds={values.channels}
            blockedChannelIds={blockedChannelIds}
            lockLastSelectedChannel={Boolean(values.creative?.length)}
            helperText={PACKAGE_CHANNEL_TOGGLE_HELPER_TEXT}
            onChange={handleChannelChange}
          />
          {touched.channels && errors.channels && (
            <FormHelperText error>{errors.channels as string}</FormHelperText>
          )}
        </div>
      )}

      <div className="bulk-selection-actions-section">
        <Grid container spacing={2}>
          <Grid
            item
            size={{
              sm: 12,
              md: 8,
            }}
          >
            <Typography variant="caption">
              <span className="fw-b">Pro tip:</span> If you want to make minor
              changes to individual locations, first make bulk selections and
              then edit individual locations.
            </Typography>
          </Grid>
          <Grid
            item
            size={{
              sm: 12,
              md: 4,
            }}
            className="d-flex justify-content-md-center justify-content-lg-end align-items-center"
          >
            <Button
              onClick={handleButtonClick}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              Apply to Selected Locations Below
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

export default JoinCampaignBulkSelectionForm;
